import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import MissionService from '../../../../../../../service/MissionService.js';
import { showToastMessage, preventKeyInputs, validateNumberWithoutDecimal, truncateNumber } from '../../../../../../../utils/commonFunctions.js';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import { useDraggable } from '../../../../hooks/useDraggable';
import UtcDateTimeField from '../../../SettingLayer/components/UtcDateTimeField';
import { displayToEpoch, epochToDisplay, getUtcNowDisplay, validateUtcDisplay } from '../../../../lib/datetime';
import { buildPendingAccessAnalysisReport } from '../../../../lib/accessAnalysisReports';
import type { SatGroup, Satellite } from '../../../../types/mission';
import styles from './AnalyserPanel.module.scss';
import { DEFAULT_PROPAGATION_STEP_SECONDS } from '../../../../lib/modelDefaults';
import { CloseSvg, ToggleMinimizeSvg } from '../../../../../../CommonComponents/svgs/Svgs';
import CustomToolTip from '../../../LeftSideBar/Common/CustomToolTip';

// Crisp Access Analysis Header Icon (exact radar sweeper matching bottom command bar)
const AnalyserHeaderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none">
    <g clipPath="url(#clip0_43212_37084_hdr)">
      <path d="M15.6205 1.72698L14.7179 2.62954C13.328 1.73982 11.7149 1.26125 10.0647 1.24906C8.4145 1.23687 6.79449 1.69156 5.39163 2.56065C3.98878 3.42974 2.86027 4.67781 2.13635 6.1608C1.41244 7.64379 1.12263 9.30126 1.30037 10.9419C1.47811 12.5826 2.11615 14.1395 3.14086 15.4331C4.16557 16.7266 5.53518 17.7041 7.09161 18.2525C8.64804 18.801 10.3278 18.8983 11.9372 18.5329C13.5465 18.1676 15.0197 17.3547 16.1867 16.188C16.3046 16.0741 16.4625 16.0111 16.6264 16.0125C16.7903 16.014 16.9471 16.0797 17.0629 16.1956C17.1788 16.3115 17.2446 16.4683 17.246 16.6321C17.2474 16.796 17.1844 16.796 17.0706 17.0718C15.7285 18.4134 14.0325 19.3455 12.1805 19.7592C10.3285 20.1729 8.39704 20.0511 6.61167 19.4081C4.82629 18.7652 3.2607 17.6275 2.09773 16.128C0.934756 14.6285 0.222407 12.8291 0.0438756 10.9399C-0.134656 9.0507 0.227997 7.14969 1.08948 5.45889C1.95097 3.76809 3.27573 2.35728 4.90905 1.39124C6.54237 0.425196 8.41683 -0.0562111 10.3135 0.00323906C12.2102 0.0626892 14.0508 0.660543 15.6205 1.72698ZM11.9515 5.39598L12.8903 4.45717C11.5661 3.76892 10.0394 3.57699 8.586 3.91604C7.13264 4.25509 5.84846 5.10277 4.9655 6.30592C4.08253 7.50908 3.659 8.98836 3.77142 10.4765C3.88384 11.9646 4.52481 13.3635 5.57851 14.4204C6.15894 15.0009 6.84805 15.4615 7.6065 15.7757C8.36494 16.0899 9.17786 16.2517 9.99882 16.2517C10.8198 16.2517 11.6327 16.0899 12.3911 15.7757C13.1496 15.4615 13.8387 15.0009 14.4191 14.4204C14.4772 14.3622 14.5233 14.2933 14.5548 14.2173C14.5862 14.1414 14.6024 14.06 14.6024 13.9778C14.6024 13.8956 14.5862 13.8143 14.5548 13.7383C14.5233 13.6624 14.4772 13.5934 14.4191 13.5353C14.361 13.4772 14.292 13.4311 14.2161 13.3996C14.1402 13.3682 14.0588 13.352 13.9766 13.352C13.8944 13.352 13.813 13.3682 13.7371 13.3996C13.6612 13.4311 13.5922 13.4772 13.5341 13.5353C12.8925 14.1768 12.0884 14.6316 11.208 14.8511C10.3277 15.0705 9.40421 15.0463 8.53656 14.7811C7.66891 14.5158 6.88979 14.0194 6.28268 13.3452C5.67556 12.6709 5.26333 11.8442 5.09016 10.9536C4.91699 10.063 4.9894 9.14205 5.29964 8.28944C5.60987 7.43683 6.14623 6.6847 6.85126 6.11363C7.55629 5.54256 8.40341 5.17407 9.30186 5.04765C10.2003 4.92123 11.1162 5.04164 11.9515 5.39598ZM17.3168 2.6833C17.434 2.80051 17.4998 2.95946 17.4998 3.1252C17.4998 3.29094 17.434 3.44989 17.3168 3.56711L11.2064 9.67503C11.2779 9.94113 11.2596 10.2234 11.1542 10.478C11.0489 10.7326 10.8625 10.9453 10.6239 11.0832C10.3854 11.221 10.108 11.2763 9.83479 11.2404C9.56161 11.2044 9.30792 11.0794 9.11309 10.8845C8.91826 10.6897 8.79318 10.436 8.75726 10.1628C8.72134 9.88965 8.77659 9.61225 8.91444 9.37367C9.05228 9.1351 9.26501 8.94869 9.51963 8.84338C9.77424 8.73806 10.0565 8.71973 10.3226 8.79122L16.4318 2.6808C16.4898 2.62259 16.5588 2.57641 16.6347 2.5449C16.7107 2.51339 16.7921 2.49717 16.8743 2.49717C16.9565 2.49717 17.0379 2.51339 17.1139 2.5449C17.1898 2.57641 17.2588 2.62259 17.3168 2.6808M18.7494 14.3754C18.7494 14.5411 18.6836 14.7001 18.5664 14.8173C18.4491 14.9345 18.2902 15.0004 18.1244 15.0004C17.9586 15.0004 17.7996 14.9345 17.6824 14.8173C17.5652 14.7001 17.4993 14.5411 17.4993 14.3754C17.4993 14.2096 17.5652 14.0506 17.6824 13.9334C17.7996 13.8162 17.9586 13.7503 18.1244 13.7503C18.2902 13.7503 18.4491 13.8162 18.5664 13.9334C18.6836 14.0506 18.7494 14.2096 18.7494 14.3754ZM18.1244 6.24979C18.2902 6.24979 18.4491 6.18394 18.5664 6.06672C18.6836 5.9495 18.7494 5.79052 18.7494 5.62475C18.7494 5.45898 18.6836 5.3 18.5664 5.18278C18.4491 5.06556 18.2902 4.99971 18.1244 4.99971C17.9586 4.99971 17.7996 5.06556 17.6824 5.18278C17.5652 5.3 17.4993 5.45898 17.4993 5.62475C17.4993 5.79052 17.5652 5.9495 17.6824 6.06672C17.7996 6.18394 17.9586 6.24979 18.1244 6.24979ZM19.9995 11.5627C19.9995 11.7284 19.9337 11.8874 19.8164 12.0046C19.6992 12.1219 19.5402 12.1877 19.3745 12.1877C19.2087 12.1877 19.0497 12.1219 18.9325 12.0046C18.8153 11.8874 18.7494 11.7284 18.7494 11.5627C18.7494 11.3969 18.8153 11.2379 18.9325 11.1207C19.0497 11.0035 19.2087 10.9376 19.3745 10.9376C19.5402 10.9376 19.6992 11.0035 19.8164 11.1207C19.9337 11.2379 19.9995 11.3969 19.9995 11.5627ZM19.3745 9.06249C19.5402 9.06249 19.6992 8.99664 19.8164 8.87942C19.9337 8.7622 19.9995 8.60322 19.9995 8.43745C19.9995 8.27167 19.9337 8.11269 19.8164 7.99547C19.6992 7.87825 19.5402 7.8124 19.3745 7.8124C19.2087 7.8124 19.0497 7.87825 18.9325 7.99547C18.8153 8.11269 18.7494 8.27167 18.7494 8.43745C18.7494 8.60322 18.8153 8.7622 18.9325 8.87942C19.0497 8.99664 19.2087 9.06249 19.3745 9.06249ZM16.2493 11.5627C16.2493 11.7284 16.1834 11.8874 16.0662 12.0046C15.949 12.1219 15.79 12.1877 15.6242 12.1877C15.4584 12.1877 15.2995 12.1219 15.1822 12.0046C15.065 11.8874 14.9992 11.7284 14.9992 11.5627C14.9992 11.3969 15.065 11.2379 15.1822 11.1207C15.2995 11.0035 15.4584 10.9376 15.6242 10.9376C15.79 10.9376 15.949 11.0035 16.0662 11.1207C16.1834 11.2379 16.2493 11.3969 16.2493 11.5627ZM15.6242 9.06249C15.79 9.06249 15.949 8.99664 16.0662 8.87942C16.1834 8.7622 16.2493 8.60322 16.2493 8.43745C16.2493 8.27167 16.1834 8.11269 16.0662 7.99547C15.949 7.87825 15.79 7.8124 15.6242 7.8124C15.4584 7.8124 15.2995 7.87825 15.1822 7.99547C15.065 8.11269 14.9992 8.27167 14.9992 8.43745C14.9992 8.60322 15.065 8.7622 15.1822 8.87942C15.2995 8.99664 15.4584 9.06249 15.6242 9.06249Z" fill="#F0F0F0" />
    </g>
    <defs>
      <clipPath id="clip0_43212_37084_hdr">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

// Crisp Satellite Pool Icon (16x16)
const SatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M10.298 5.70199L8.58563 3.98965C8.52481 3.92883 8.42578 3.92854 8.36871 3.9929C7.89645 4.52547 7.68424 5.20801 7.73178 5.87638M7.73178 5.87638C7.77429 6.47399 8.02446 7.06027 8.48208 7.51789C8.91501 7.95083 9.55969 8.20678 10.1236 8.26819C10.8221 8.34426 11.4605 8.12754 12.0077 7.63188C12.0714 7.57414 12.0711 7.47516 12.0103 7.41433L10.298 5.70199M10.298 5.70199L11.2319 4.76805M7.73178 5.87638L6.69531 6.91285M10.1236 8.26819L9.08712 9.30466M6.69531 6.91285L5.23329 5.45083C5.17247 5.39001 5.07386 5.39001 5.01303 5.45083L3.25927 7.2046C3.19844 7.26542 3.19845 7.36404 3.25927 7.42486L4.72129 8.88688M6.69531 6.91285L4.72129 8.88688M4.72129 8.88688L4.30631 9.30185C3.90083 9.70734 3.90083 10.3648 4.30631 10.7702L5.22973 11.6937C5.63521 12.0991 6.29263 12.0991 6.69812 11.6937L7.11309 11.2787M9.08712 9.30466L7.11309 11.2787M9.08712 9.30466L10.5491 10.7667C10.61 10.8275 10.61 10.9261 10.5491 10.9869L8.79537 12.7407C8.73455 12.8015 8.63593 12.8015 8.57511 12.7407L7.11309 11.2787" stroke="#b4b4b4" strokeWidth="0.8" strokeLinecap="round" />
  </svg>
);

// Crisp GeoFeature Pool Icon (16x16)
const GeoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M5.55753 5.70877C5.45903 5.23699 5.04077 4.8826 4.53974 4.8826C3.96551 4.8826 3.5 5.34811 3.5 5.92235C3.5 6.49658 3.96551 6.96209 4.53974 6.96209C5.11398 6.96209 5.57949 6.49658 5.57949 5.92235C5.57949 5.84914 5.57192 5.7777 5.55753 5.70877ZM4.53974 6.96209V10.4194M9.06028 11.4592C9.06028 12.0334 9.52579 12.4989 10.1 12.4989C10.6743 12.4989 11.1398 12.0334 11.1398 11.4592C11.1398 10.9481 10.7711 10.5232 10.2852 10.4359M9.06028 11.4592C9.06028 10.8849 9.52579 10.4194 10.1 10.4194C10.1632 10.4194 10.2251 10.4251 10.2852 10.4359M9.06028 11.4592L5.57949 11.4591M5.57949 11.4591C5.57949 12.0333 5.11398 12.4988 4.53974 12.4988C3.96551 12.4988 3.5 12.0333 3.5 11.4591C3.5 10.8849 3.96551 10.4194 4.53974 10.4194M5.57949 11.4591C5.57949 10.8849 5.11398 10.4194 4.53974 10.4194M10.2852 10.4359L11.2523 5.56003M10.4374 4.72883C10.5135 5.14579 10.8382 5.47596 11.2523 5.56003C11.3195 5.57367 11.389 5.58083 11.4603 5.58083C12.0345 5.58083 12.5 5.11532 12.5 4.54109C12.5 3.96685 12.0345 3.50134 11.4603 3.50134C10.886 3.50134 10.4205 3.96685 10.4205 4.54109C10.4205 4.60519 10.4263 4.66793 10.4374 4.72883ZM5.55753 5.70877L10.4374 4.72883" stroke="#b4b4b4" strokeWidth="0.8" />
  </svg>
);

// Crisp Ground Station Dish Icon (16x16)
const DishIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M9.71077 2.50205C9.57356 2.51336 9.4715 2.63375 9.4828 2.77097C9.49411 2.90818 9.61451 3.01025 9.75171 2.99894L9.73124 2.7505L9.71077 2.50205ZM10.9652 3.10889L10.8148 3.30772L10.9652 3.10889ZM11.4014 4.24663C11.4279 4.38174 11.5589 4.4698 11.694 4.44331C11.8291 4.41683 11.9171 4.28584 11.8907 4.15073L11.646 4.19868L11.4014 4.24663ZM9.58363 3.25339C9.44708 3.27095 9.35062 3.39588 9.36818 3.53244C9.38574 3.66899 9.51067 3.76546 9.64722 3.7479L9.61543 3.50065L9.58363 3.25339ZM10.4926 3.73383L10.643 3.535L10.4926 3.73383ZM10.7092 4.55111C10.7295 4.68728 10.8563 4.78123 10.9925 4.76094C11.1287 4.74066 11.2226 4.61382 11.2023 4.47765L10.9558 4.51438L10.7092 4.55111ZM9.73124 2.7505L9.75171 2.99894C10.132 2.96761 10.5105 3.07754 10.8148 3.30772L10.9652 3.10889L11.1156 2.91006C10.7134 2.60591 10.2133 2.46065 9.71077 2.50205L9.73124 2.7505ZM10.9652 3.10889L10.8148 3.30772C11.1192 3.53789 11.328 3.87217 11.4014 4.24663L11.646 4.19868L11.8907 4.15073C11.7937 3.65592 11.5177 3.21422 11.1156 2.91006L10.9652 3.10889ZM9.61543 3.50065L9.64722 3.7479C9.89414 3.71615 10.1437 3.78248 10.3422 3.93265L10.4926 3.73383L10.643 3.535C10.3403 3.30611 9.95999 3.205 9.58363 3.25339L9.61543 3.50065ZM10.4926 3.73383L10.3422 3.93265C10.5408 4.08283 10.6725 4.30487 10.7092 4.55111L10.9558 4.51438L11.2023 4.47765C11.1464 4.10233 10.9456 3.76389 10.643 3.535L10.4926 3.73383ZM6.29919 3.87141C6.16194 3.7676 5.96651 3.79472 5.86271 3.93198C5.7589 4.06924 5.78602 4.26467 5.92328 4.36848L6.11124 4.11995L6.29919 3.87141ZM11.0865 8.27347C11.2237 8.37728 11.4191 8.35017 11.523 8.21291C11.6268 8.07564 11.5996 7.88022 11.4624 7.77641L11.2744 8.02494L11.0865 8.27347ZM10.042 4.80501C10.1458 4.66775 10.1187 4.47232 9.98147 4.36851C9.84421 4.2647 9.64879 4.29182 9.54498 4.42908L9.79351 4.61705L10.042 4.80501ZM6.11124 4.11995L5.92328 4.36848L6.19997 4.57775L6.38793 4.32922L6.57589 4.08068L6.29919 3.87141L6.11124 4.11995ZM6.96111 8.36225L7.14907 8.11371C6.48286 7.60985 6.11942 7.03592 6.03033 6.44696C5.9415 5.85968 6.11731 5.20364 6.63646 4.51718L6.38793 4.32922L6.13941 4.14125C5.5449 4.92735 5.29441 5.74866 5.41414 6.54017C5.53361 7.33 6.01358 8.03631 6.77315 8.61078L6.96111 8.36225ZM11.2744 8.02494L11.4624 7.77641L11.1857 7.56713L10.9977 7.81567L10.8098 8.0642L11.0865 8.27347L11.2744 8.02494ZM6.96111 8.36225L6.77315 8.61078C7.53272 9.18525 8.34301 9.45479 9.13551 9.35469C9.92969 9.25439 10.6517 8.78973 11.2463 8.00363L10.9977 7.81567L10.7492 7.6277C10.23 8.31416 9.64668 8.66196 9.05742 8.73639C8.46647 8.81103 7.81528 8.61758 7.14907 8.11371L6.96111 8.36225ZM6.38793 4.32922L6.19997 4.57775L8.50487 6.32098L8.69283 6.07244L8.88079 5.82391L6.57589 4.08068L6.38793 4.32922ZM10.9977 7.81567L11.1857 7.56713L8.88079 5.82391L8.69283 6.07244L8.50487 6.32098L10.8098 8.0642L10.9977 7.81567ZM8.69283 6.07244L8.94136 6.26041L10.042 4.80501L9.79351 4.61705L9.54498 4.42908L8.4443 5.88448L8.69283 6.07244Z" fill="#b4b4b4" />
    <path d="M9.83496 11.9504L9.48804 11.9504M9.48804 11.9504L7.02785 11.9504L4.13801 11.9504M9.48804 11.9504L5.36821 8.47294C4.882 8.06254 4.138 8.40817 4.138 9.04444L4.13801 11.9504M3.79109 11.9504L4.13801 11.9504" stroke="#b4b4b4" strokeWidth="0.8" strokeLinecap="round" />
    <ellipse cx="5.14928" cy="9.64547" rx="0.397326" ry="0.397336" fill="#b4b4b4" />
  </svg>
);

const ChipRemoveIcon = () => (
  <svg width="9.6" height="9.6" viewBox="0 0 10 10" fill="none">
    <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

type SourceObjectType = 'Single' | 'Group';
type PoolItemKind = 'single' | 'group' | 'geofeature' | 'groundstation';
type PoolItem = { id: string; requestObjectId?: string; name: string; kind?: PoolItemKind; objectType?: SourceObjectType };

function getAnalysisSources(tree: Array<Satellite | SatGroup>): PoolItem[] {
  const out: PoolItem[] = [];
  for (const item of tree) {
    if ((item as SatGroup).type === 'group') {
      const grp = item as SatGroup;
      out.push({ id: grp.id, name: grp.name, kind: 'group', objectType: 'Group' });
      if (Array.isArray(grp.children)) {
        grp.children.forEach(child => {
          out.push({
            id: child.id,
            requestObjectId: child.id,
            name: child.name,
            kind: 'single',
            objectType: 'Single',
          });
        });
      }
    } else {
      const satellite = item as Satellite;
      out.push({
        id: satellite.id,
        requestObjectId: satellite.id,
        name: satellite.name,
        kind: 'single',
        objectType: 'Single',
      });
    }
  }
  return out;
}

interface PoolProps {
  title: string;
  type: 'satellites' | 'geofeatures' | 'groundstations';
  icon: React.ReactNode;
  selected: PoolItem[];
  onRemove: (id: string) => void;
  allSatellites?: Array<Satellite | SatGroup>;
}

const Pool: React.FC<PoolProps> = ({ title, type, icon, selected, onRemove, allSatellites }) => {
  const placeholderText = type === 'satellites'
    ? 'Select Satellite(s)'
    : type === 'geofeatures'
      ? 'Select Geofeature(s)'
      : 'Select Ground Station(s)';

  const displayCount = useMemo(() => {
    if (type !== 'satellites' || !allSatellites) return selected.length;

    let count = 0;
    selected.forEach(item => {
      if (item.objectType === 'Group') {
        const grp = (allSatellites || []).find(x => x.id === item.id) as SatGroup;
        if (grp && Array.isArray(grp.children)) {
          count += grp.children.length;
        }
      } else {
        count += 1;
      }
    });
    return count;
  }, [selected, type, allSatellites]);

  return (
    <div className={styles.pool}>
      <div className={styles.poolHeaderRow}>
        <div className={styles.poolHeaderLeft}>
          <span className={styles.poolIcon}>{icon}</span>
          <span className={styles.poolTitle}>{title} ({displayCount})</span>
        </div>
      </div>

      <div className={styles.poolBox}>
        {selected.length === 0 ? (
          <span className={styles.poolPlaceholder}>{placeholderText}</span>
        ) : (
          <div className={styles.poolChips}>
            {selected.map(item => (
              <span
                key={item.id}
                className={`${styles.poolChip} ${styles[type]}`}
              >
                <span className={styles.chipText}>{item.name}</span>
                <button
                  className={styles.chipRemove}
                  type="button"
                  onClick={() => onRemove(item.id)}
                >
                  <ChipRemoveIcon />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AnalyserPanel: React.FC = () => {
  const {
    analyserOpen, closeAnalyser, satellites, geoFeatures, groundStations,
    selectedItemId, selectedItemType, modelData, accessAnalysisReports, upsertAccessAnalysisReport,
    closeAddGS, selectTreeItem, leftPanelCollapsed,
    selectedGeoFeatureIds, toggleSelectedGeoFeature,
  } = useMDCockpit();
  const { mission_id, scenario_id } = useParams<{ mission_id: string; scenario_id: string }>();
  const { style, headerProps } = useDraggable({
    top: 60,
    left: leftPanelCollapsed ? 12 : 236,
  }, analyserOpen);

  const [minimized, setMinimized] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (analyserOpen) {
      setMinimized(false);
    }
  }, [analyserOpen]);

  const [selSourceIds, setSelSourceIds] = useState<Set<string>>(new Set());
  const [selGfIds, setSelGfIds] = useState<Set<string>>(new Set());
  const [selGsIds, setSelGsIds] = useState<Set<string>>(new Set());

  const [startTime, setStartTime] = useState(getUtcNowDisplay);
  const [durationHrs, setDurationHrs] = useState('12');
  const [durationError, setDurationError] = useState('');
  const [stepSec, setStepSec] = useState(String(DEFAULT_PROPAGATION_STEP_SECONDS));
  const lastToastTimeRef = useRef(0);
  const showThrottledInfoToast = (msg: string) => {
    const now = Date.now();
    if (now - lastToastTimeRef.current > 3000) {
      showToastMessage('info', msg);
      lastToastTimeRef.current = now;
    }
  };

  const allSources = useMemo(() => getAnalysisSources(satellites as Array<Satellite | SatGroup>), [satellites]);
  const sourceById = useMemo(() => new Map(allSources.map(source => [source.id, source])), [allSources]);

  const allGfs = useMemo(() => geoFeatures.map((g: any) => ({ id: g.id, name: g.name, kind: 'geofeature' as const })), [geoFeatures]);
  const allGs = useMemo(() => groundStations.map((g: any) => ({ id: g.id, name: g.name, kind: 'groundstation' as const })), [groundStations]);

  useEffect(() => {
    if (!analyserOpen) return;
    const prop = modelData?.propagation as any;
    if (prop?.startEpoch) setStartTime(epochToDisplay(prop.startEpoch));
    if (prop?.startEpoch && prop?.endEpoch && prop.endEpoch > prop.startEpoch) {
      const hours = (prop.endEpoch - prop.startEpoch) / 3600;
      setDurationHrs(Number.isInteger(hours) ? String(hours) : String(Number(hours.toFixed(4))));
    }
    if (prop?.stepSizeSeconds && prop.stepSizeSeconds >= 1 && prop.stepSizeSeconds <= 10) setStepSec(String(prop.stepSizeSeconds));
  }, [analyserOpen, modelData?.propagation]);

  const lastProcessedIdRef = useRef<string | null>(null);
  const ignoreNextSelectionChangeRef = useRef<boolean>(false);

  useEffect(() => {
    if (!analyserOpen) return;

    if (ignoreNextSelectionChangeRef.current) {
      ignoreNextSelectionChangeRef.current = false;
      lastProcessedIdRef.current = selectedItemId;
      return;
    }

    if (!selectedItemId || !selectedItemType) {
      if (lastProcessedIdRef.current) {
        const prevId = lastProcessedIdRef.current;
        setSelSourceIds(prev => {
          const n = new Set(prev);
          n.delete(prevId);
          return n;
        });
        setSelGfIds(prev => {
          const n = new Set(prev);
          n.delete(prevId);
          return n;
        });
        setSelGsIds(prev => {
          const n = new Set(prev);
          n.delete(prevId);
          return n;
        });
        lastProcessedIdRef.current = null;
      }
      return;
    }

    if (lastProcessedIdRef.current === selectedItemId) return;
    lastProcessedIdRef.current = selectedItemId;

    if (selectedItemType === 'satellite' || selectedItemType === 'group') {
      if (sourceById.has(selectedItemId)) {
        // Check if we are selecting a child satellite whose parent group is already selected
        const isParentGroupSelected = (satellites || []).some(x => {
          const grp = x as SatGroup;
          if (grp.type === 'group' && Array.isArray(grp.children)) {
            return selSourceIds.has(grp.id) && grp.children.some(c => c.id === selectedItemId);
          }
          return false;
        });

        if (isParentGroupSelected) {
          // Immediately clear the tree selection so it doesn't show as highlighted
          selectTreeItem(null, null);
          return;
        }

        const alreadyInPool = selSourceIds.has(selectedItemId);
        if (alreadyInPool) {
          setSelSourceIds(prev => {
            const n = new Set(prev);
            n.delete(selectedItemId);
            return n;
          });
          selectTreeItem(null, null);
        } else {
          setSelSourceIds(prev => {
            const n = new Set(prev);
            n.add(selectedItemId);

            const clean = new Set(n);
            (satellites || []).forEach(item => {
              const grp = item as SatGroup;
              if (grp.type === 'group' && clean.has(grp.id)) {
                if (Array.isArray(grp.children)) {
                  grp.children.forEach(child => {
                    clean.delete(child.id);
                  });
                }
              }
            });
            return clean;
          });
        }
      }
    } else if (selectedItemType === 'geofeature') {
      const alreadyInPool = selGfIds.has(selectedItemId);
      if (alreadyInPool) {
        setSelGfIds(prev => {
          const n = new Set(prev);
          n.delete(selectedItemId);
          return n;
        });
        selectTreeItem(null, null);
      } else {
        setSelGfIds(prev => {
          const n = new Set(prev);
          n.add(selectedItemId);
          return n;
        });
      }
    } else if (selectedItemType === 'groundstation') {
      const alreadyInPool = selGsIds.has(selectedItemId);
      if (alreadyInPool) {
        setSelGsIds(prev => {
          const n = new Set(prev);
          n.delete(selectedItemId);
          return n;
        });
        selectTreeItem(null, null);
      } else {
        setSelGsIds(prev => {
          const n = new Set(prev);
          n.add(selectedItemId);
          return n;
        });
      }
    }
  }, [selectedItemId, selectedItemType, analyserOpen, sourceById]);

  const startEpoch = displayToEpoch(startTime);
  const durationNumber = Number(durationHrs);
  const stepSecNumber = Number(stepSec);

  const modelStart = modelData?.propagation?.startEpoch;
  const modelEnd = modelData?.propagation?.endEpoch;

  const startTimeError = useMemo(() => {
    const fmtErr = validateUtcDisplay(startTime);
    if (fmtErr) return fmtErr === 'Required' ? '' : fmtErr;
    if (startEpoch !== null && modelStart && modelEnd) {
      if (startEpoch < modelStart) {
        return `Start time cannot be earlier than ${epochToDisplay(modelStart)} UTC`;
      }
      if (startEpoch > modelEnd) {
        return `Start time cannot be later than ${epochToDisplay(modelEnd)} UTC`;
      }
    }
    return '';
  }, [startTime, startEpoch, modelStart, modelEnd]);

  const getDurationErrorText = (durationStr: string, startEpochVal: number | null, modelEndVal?: number, modelStartVal?: number): string => {
    if (!durationStr.trim()) return 'Duration is required';
    const num = Number(durationStr);
    if (!Number.isFinite(num) || num <= 0) return 'Duration should be greater than or equal to 1';
    if (startEpochVal !== null && modelStartVal && modelEndVal) {
      const maxAllowedSec = modelEndVal - startEpochVal;
      const maxAllowedHrs = maxAllowedSec / 3600;
      if (num * 3600 > maxAllowedSec) {
        const formattedMax = Number.isInteger(maxAllowedHrs) ? String(maxAllowedHrs) : String(Number(truncateNumber(maxAllowedHrs, 3)));
        return `Duration cannot exceed remaining model duration (${formattedMax} Hrs)`;
      }
    }
    return '';
  };

  useEffect(() => {
    if (!analyserOpen) return;
    const err = getDurationErrorText(durationHrs, startEpoch, modelEnd, modelStart);
    setDurationError(err);
  }, [analyserOpen, durationHrs, startEpoch, modelEnd, modelStart]);

  const stepSecError = useMemo(() => {
    if (!stepSec.trim()) return 'Step size is required';
    if (Number(stepSecNumber) < 0 || Number(stepSecNumber) > 10) {
      return 'Step size should be between 1 and 10';
    }
    return '';
  }, [stepSec, stepSecNumber]);

  const handleStepSecChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    if (raw === '') {
      setStepSec('');
      return;
    }
    const num = Number(raw);
    if (num > 0 && num <= 10) {
      setStepSec(raw);
    } else {
      showThrottledInfoToast('Propagation step size should be between 1 and 10');
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const sanitized = val.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1');
    const err = getDurationErrorText(sanitized, startEpoch, modelEnd, modelStart);
    if (err && sanitized.trim() !== '') {
      showThrottledInfoToast(err);
      setDurationError(err);
      return;
    }
    setDurationHrs(sanitized);
    setDurationError(err);
  };

  // Reset all local state when the panel transitions from open → closed
  const wasAnalyserOpenRef = useRef(false);
  useEffect(() => {
    if (analyserOpen) {
      wasAnalyserOpenRef.current = true;
      return;
    }
    if (!wasAnalyserOpenRef.current) return;
    wasAnalyserOpenRef.current = false;
    setSelSourceIds(new Set());
    setSelGfIds(new Set());
    setSelGsIds(new Set());
    setStartTime(getUtcNowDisplay());
    setDurationHrs('12');
    setDurationError('');
    setStepSec(String(DEFAULT_PROPAGATION_STEP_SECONDS));
    setMinimized(false);
    setRunning(false);
    selectTreeItem(null, null);
    lastProcessedIdRef.current = null;
  }, [analyserOpen]);

  if (!analyserOpen) return null;

  const selSources = allSources.filter(s => selSourceIds.has(s.id));
  const selGfs = allGfs.filter(g => selGfIds.has(g.id));
  const selGs = allGs.filter(g => selGsIds.has(g.id));

  const isRequiredEmpty =
    selSources.length === 0 ||
    (selGfs.length === 0 && selGs.length === 0) ||
    !startTime.trim() ||
    !durationHrs.trim() ||
    !stepSec.trim();

  const runDisabled = running || isRequiredEmpty;

  const handleRun = async () => {
    if (runDisabled) return;

    if (startTimeError || validateUtcDisplay(startTime) !== '' || startEpoch === null) {
      showToastMessage('info', startTimeError || 'Invalid Start Time. Expected format: YYYY-MM-DD HH:MM:SS (UTC)');
      return;
    }

    if (!Number.isFinite(durationNumber) || durationNumber <= 0 || durationError) {
      showToastMessage('info', durationError || 'Duration should be greater than or equal to 1');
      return;
    }

    if (!Number.isFinite(stepSecNumber) || stepSecNumber < 1 || stepSecNumber > 10 || stepSecError) {
      showToastMessage('info', stepSecError || 'Propagation step size should be between 1 and 10');
      return;
    }

    if (!mission_id || !scenario_id) {
      showToastMessage('info', !mission_id ? 'Mission Id is required' : 'Scenario Id is required');
      return;
    }

    setRunning(true);
    closeAddGS();
    selectTreeItem(null, null);

    const body = {
      groups: selSources.map(source => ({
        objectId: source.requestObjectId ?? source.id,
        objectType: source.objectType ?? 'Single',
      })),
      groundstationIds: selGs.map(g => g.id),
      aoiIds: selGfIds.size > 0 ? Array.from(selGfIds) : [],
      startTime: startEpoch,
      duration: durationNumber,
      stepSec: Math.round(stepSecNumber),
    };

    try {
      const res = await MissionService.runAccessAnalysis(mission_id, scenario_id, body);
      const data = res?.data?.data ?? res?.data;
      const responseReport = data?.report ?? data?.data ?? null;
      const initialReport = responseReport ?? buildPendingAccessAnalysisReport({
        accessAnalysisId: data?.accessAnalysisId ?? data?.id,
        startTime: startEpoch,
        durationHours: durationNumber,
        stepSizeSeconds: stepSecNumber,
        satellites: selSources.map((s) => ({ id: s.id, name: s.name })),
        groundStations: selGs.map((g) => ({ id: g.id, name: g.name })),
        geoFeatures: selGfs.map((g) => ({ id: g.id, name: g.name })),
      });
      if (initialReport) {
        upsertAccessAnalysisReport(initialReport);
      }
      showToastMessage('success', data?.message ?? res?.data?.message ?? 'Access analysis started');
      setRunning(false);
      closeAddGS();
      selectTreeItem(null, null);
      closeAnalyser();
    } catch (error) {
      const message = (error as any)?.response?.data?.message ?? (error as Error)?.message ?? 'Access analysis failed';
      showToastMessage('error', message);
      setRunning(false);
    }
  };

  return (
    <div className={styles.panel} style={{ ...style, zIndex: 30, width: 320 }}>
      {/* Header — drag handle */}
      <div className={styles.header} {...headerProps}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <AnalyserHeaderIcon />
          </div>
          <span className={styles.headerTitle}>Access Analysis</span>
        </div>
        <div className={styles.headerRight}>
          <CustomToolTip title={minimized ? 'Restore' : 'Minimize'}>
            <button className={styles.headerBtn} type="button" onClick={() => setMinimized(m => !m)}>
              <ToggleMinimizeSvg type={minimized ? 'maximize' : 'minimize'} />
            </button>
          </CustomToolTip>
          <CustomToolTip title="Close">
            <button className={styles.headerBtn} type="button" onClick={closeAnalyser}>
              <CloseSvg />
            </button>
          </CustomToolTip>
        </div>
      </div>

      <div className={`${styles.collapseWrapper} ${minimized ? styles.collapsed : ''}`}>
        <>
          <div className={styles.body}>
            {/* Satellites Pool Section */}
            <div className={styles.sectionBlock}>
              <Pool
                title="Satellites"
                type="satellites"
                icon={<SatIcon />}
                selected={selSources}
                allSatellites={satellites as Array<Satellite | SatGroup>}
                onRemove={id => {
                  setSelSourceIds(s => { const n = new Set(s); n.delete(id); return n; });
                  if (selectedItemId === id) {
                    ignoreNextSelectionChangeRef.current = true;
                    selectTreeItem(null, null);
                  } else {
                    const isChildOfRemovedGroup = (satellites || []).some(x => {
                      const grp = x as SatGroup;
                      return grp.type === 'group' && grp.id === id && Array.isArray(grp.children) && grp.children.some(c => c.id === selectedItemId);
                    });
                    const isParentOfRemovedChild = (satellites || []).some(x => {
                      const grp = x as SatGroup;
                      return grp.type === 'group' && grp.id === selectedItemId && Array.isArray(grp.children) && grp.children.some(c => c.id === id);
                    });
                    if (isChildOfRemovedGroup || isParentOfRemovedChild) {
                      ignoreNextSelectionChangeRef.current = true;
                      selectTreeItem(null, null);
                    }
                  }
                }}
              />
            </div>

            {/* Geofeatures Pool Section */}
            <div className={styles.sectionBlock}>
              <Pool
                title="Geofeatures"
                type="geofeatures"
                icon={<GeoIcon />}
                selected={selGfs}
                onRemove={id => {
                  setSelGfIds(s => { const n = new Set(s); n.delete(id); return n; });
                  if (selectedGeoFeatureIds?.includes(id)) {
                    const nextIds = selectedGeoFeatureIds.filter((selectedId) => selectedId !== id);
                    const nextSelectedItemId = nextIds.length > 0 ? nextIds[nextIds.length - 1] : null;
                    if (nextSelectedItemId !== selectedItemId) {
                      ignoreNextSelectionChangeRef.current = true;
                    }
                    toggleSelectedGeoFeature(id);
                  } else if (selectedItemId === id) {
                    ignoreNextSelectionChangeRef.current = true;
                    selectTreeItem(null, null);
                  }
                }}
              />
            </div>

            {/* Ground Stations Pool Section */}
            <div className={styles.sectionBlock}>
              <Pool
                title="Ground Station"
                type="groundstations"
                icon={<DishIcon />}
                selected={selGs}
                onRemove={id => {
                  setSelGsIds(s => { const n = new Set(s); n.delete(id); return n; });
                  if (selectedItemId === id) {
                    ignoreNextSelectionChangeRef.current = true;
                    selectTreeItem(null, null);
                  }
                }}
              />
            </div>

            {/* Simulation Resolution Section */}
            <div className={`${styles.sectionBlock} ${styles.withDivider}`}>
              <div className={styles.sectionTitleRow}>
                <span className={styles.sectionTitle}>Simulation Resolution</span>
              </div>
              <div className={styles.rowsList}>
                <div className={styles.inputRow}>
                  <span className={styles.rowLabel}>Propagation Step Size</span>
                  <div className={styles.inputRoot}>
                    <div className={styles.textContainer}>
                      <input
                        className={`${styles.fieldInput} ${stepSecError ? styles.fieldInputError : ''}`}
                        type="text"
                        inputMode="numeric"
                        value={stepSec}
                        onKeyDown={validateNumberWithoutDecimal}
                        onChange={handleStepSecChange}
                      />
                    </div>
                    <span className={styles.unitLabel}>s</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scenario Details Section */}
            <div className={styles.sectionBlock}>
              <div className={styles.sectionTitleRow}>
                <span className={styles.sectionTitle}>Scenario Details</span>
              </div>
              <div className={styles.rowsList}>
                <div className={styles.inputRow}>
                  <span className={styles.rowLabel}>Start Time</span>
                  <div className={styles.inputRoot}>
                    <div className={styles.textContainer}>
                      <div className={styles.dateTimePickerWrap}>
                        <UtcDateTimeField
                          value={startTime}
                          onChange={(display) => setStartTime(display)}
                          error={startTimeError}
                        />
                      </div>
                    </div>
                    <span className={styles.unitLabel}>UTC</span>
                  </div>
                </div>
                <div className={styles.inputRow}>
                  <span className={styles.rowLabel}>Duration</span>
                  <div className={styles.inputRoot}>
                    <div className={styles.textContainer}>
                      <input
                        className={`${styles.fieldInput} ${durationError ? styles.fieldInputError : ''}`}
                        type="text"
                        inputMode="decimal"
                        value={durationHrs}
                        onKeyDown={e => preventKeyInputs(e, ['e', 'E', '+', '-'])}
                        onChange={handleDurationChange}
                      />
                    </div>
                    <span className={styles.unitLabel}>Hrs</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.footer}>
            <button className={styles.cancelBtn} type="button" onClick={closeAnalyser}>Cancel</button>
            <button
              className={`${styles.runBtn} ${!runDisabled ? styles.activeRun : ''} ${running ? styles.runBtnRunning : ''}`}
              type="button"
              onClick={handleRun}
              disabled={runDisabled}
            >
              {running ? 'Running…' : 'Analyze'}
            </button>
          </div>
        </>
      </div>
    </div>
  );
};

export default AnalyserPanel;
